import useDidUpdateEffect from 'new/hooks/useDidUpdateEffect';
import * as React from 'react';
import {
  ForwardedRef,
  ChangeEvent as RChangeEvent,
  FocusEvent as RFocusEvent,
  KeyboardEvent as RKeyboardEvent,
  ReactElement,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { useConst } from '../../hooks/useConst';
import { useOnComponentMount } from '../../hooks/useOnComponentMount';
import { useScope } from '../../hooks/useScope';
import { CSSBox } from '../CSSBox';
import './MacroInput.css';
import { getMinMax } from './tools/getMinMax';
import { formatAll, resultToComplex } from './tools/macroTools';
import { matchToMacro } from './tools/matchToMacro';
import { segmentsToString } from './tools/segmentsToString';
import { ComplexValue } from './types/ComplexValue';
import { Macro } from './types/Macro';
import { MacroParts } from './types/MacroParts';
import { Match } from './types/Match';
import { Result } from './types/Result';
import { SearchFilterViewsType } from 'js/constants/visibilityFilters';

const focusedClassName = 'is-focused';
const multiClassName = 'is-multi';

export interface MacroInputApi {
  setValue: (value: string, triggerEvents?: boolean) => void;
  setSegments: (segments: (string | Macro | MacroParts)[], triggerEvents?: boolean) => void;
  getValue: () => string;
  getMacros: () => Macro[];
  getTexts: () => string[];
  getCurrentComplex: () => ComplexValue;
  getCurrentMacro: () => Macro;
  getCurrentText: () => string;
  getCurrentTextInfo: () => {
    text: string;
    start: number | undefined;
    end: number | undefined;
  };
  getSegments: () => (string | Macro)[];
  getCurrentSegment: () => {
    selectionStart: number;
    selectionEnd: number;
    segment: string;
    element?: HTMLTextAreaElement;
  };
  updateCurrentMacro: (segment: string) => void;
  replaceCurrentText: (segment: string) => void;
  appendValue: (segment: string) => void;
  triggerChangeWithFakeValue: (
    value: string,
    onChange?: (e: RChangeEvent<HTMLInputElement>) => void,
  ) => void;
  triggerChangeWithCurrentSegment: (onChange?: (e: RChangeEvent<HTMLInputElement>) => void) => void;
  focus: () => void;
  blur: () => void;
  contains: (element: HTMLElement) => boolean;
}

export interface MacroInputProps {
  value?: string;
  placeholder?: string;
  multiLine?: boolean;
  before?: ReactElement;
  after?: ReactElement;
  clearSelectionOnBlur?: boolean;
  onFocus?: (e: RFocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: RFocusEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: RKeyboardEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: RKeyboardEvent<HTMLInputElement>) => void;
  onSelect?: (selection: Macro) => void;
  onDeselect?: () => void;
  onChange?: (value: ComplexValue) => void;
  onMacroChange?: (macro: Macro) => void;
  onTextChange?: (text: string) => void;
  id?: string;
  className?: string;
  style?: string;
  view: SearchFilterViewsType;
}

const MacroInputFn = (props: MacroInputProps, forwardedRef: ForwardedRef<MacroInputApi>) => {
  const {
    value = '',
    multiLine,
    before,
    after,
    clearSelectionOnBlur,
    onFocus,
    onBlur,
    onKeyUp,
    onKeyDown,
    onSelect,
    onDeselect,
    onChange,
    onMacroChange,
    onTextChange,
    style,
    view,
    ...nativeProps
  } = props;
  const invalidFieldsByView = useSelector(
    (state: any) => state.app.visibilityFilters.invalidFields,
  );
  const invalidFields = invalidFieldsByView[view];
  const [touched, setTouched] = useState(false);
  const rootRef = useRef<HTMLDivElement>();
  const areaRef = useRef<HTMLTextAreaElement>();
  const overlayRef = useRef<HTMLDivElement>();

  const scope = useScope({
    touched,
    clearSelectionOnBlur,
    onFocus,
    onBlur,
    onKeyUp,
    onKeyDown,
    onSelect,
    onDeselect,
    onChange,
    onMacroChange,
    onTextChange,
  });

  const current = useConst(() => {
    let currentComplex: ComplexValue = undefined;
    let currentResult: Result = undefined;
    let currentMatches: Match[] = [];
    let currentMatch: Match = undefined;
    let currentText: string = '';
    let currentTextStart: number | undefined = undefined;
    let currentTextEnd: number | undefined = undefined;

    return {
      complex: currentComplex,
      result: currentResult,
      matches: currentMatches,
      match: currentMatch,
      text: currentText,
      textStart: currentTextStart,
      textEnd: currentTextEnd,
    };
  });

  const actions = useConst(() => {
    const clearSelection = ({ triggerSelection = false } = {}) => {
      const overlay = overlayRef.current;

      if (!current.match) return;
      const child = overlay.children[current.match.i];

      current.match = undefined;
      if (!child) return;

      child.classList.remove(focusedClassName);

      if (triggerSelection) {
        current.complex.current = '';
        scope.onDeselect?.();
      }
    };

    const updateCurrentTextInfo = (text = '', start = undefined, end = undefined) => {
      current.text = text;
      current.textStart = start;
      current.textEnd = end;
    };

    const updateSelection = ({ triggerChanges = false, triggerSelection = false } = {}) => {
      const rootClasses = rootRef.current.classList;
      const overlay = overlayRef.current;
      const area = areaRef.current;

      const { complex } = current;

      if (!rootClasses.contains(focusedClassName) && scope.clearSelectionOnBlur) {
        clearSelection({ triggerSelection });
        return;
      }

      const { selectionStart, selectionEnd, value } = area;
      const [selectionMin, selectionMax] = getMinMax(selectionStart, selectionEnd);

      let newMatch: Match;
      let matchBefore: Match;
      let matchAfter: Match;

      for (const match of current.matches) {
        if (match.end < selectionMin) {
          matchBefore = match;
        }
        if (match.start > selectionMax) {
          matchAfter = matchAfter || match;
        }

        if (match.start > selectionMin) {
          continue;
        }
        if (match.end < selectionMax) {
          continue;
        }

        newMatch = match;
        break;
      }

      let macroHasChanged: boolean;
      $macro: {
        if (!newMatch) {
          clearSelection({ triggerSelection });
          break $macro;
        }

        if (current.match?.i === newMatch.i) {
          current.match = newMatch;
          const child = overlay.children[newMatch.i];
          child.classList.add(focusedClassName);
          break $macro;
        }

        clearSelection({ triggerSelection });

        const child = overlay.children[newMatch.i];
        child.classList.add(focusedClassName);
        // child.scrollIntoView();

        current.match = newMatch;
        macroHasChanged = true;
      }

      let textHasChanged: boolean;
      $text: {
        const oldText = current.text;

        if (newMatch || selectionMin !== selectionMax) {
          updateCurrentTextInfo();
          textHasChanged = current.text !== oldText;
          break $text;
        }

        const limitStart = matchBefore?.end || 0;
        const limitEnd = matchAfter?.start || value.length;

        // search from selection
        let searchStart = Math.max(0, selectionMin - 1);
        let searchEnd = Math.max(0, selectionMax);

        if (value[searchStart] === ' ') {
          updateCurrentTextInfo();
          textHasChanged = current.text !== oldText;
          break $text;
        }

        while ((value[searchStart - 1] || ' ') !== ' ') {
          searchStart -= 1;
          if (searchStart === limitStart) break;
        }

        while ((value[searchEnd] || ' ') !== ' ') {
          searchEnd += 1;
          if (searchEnd === limitEnd) break;
        }

        updateCurrentTextInfo(value.slice(searchStart, searchEnd), searchStart, searchEnd);
        textHasChanged = current.text !== oldText;
      }

      if (textHasChanged) {
        complex.current = current.text;

        if (triggerChanges) {
          scope.onTextChange?.(current.text);
        }
      }

      if (macroHasChanged) {
        complex.current = current.match.segment;

        if (triggerSelection) {
          const match = matchToMacro(newMatch);
          scope.onSelect?.(match);
        }
      }
    };

    const render = (
      { triggerChanges = false, triggerSelection = false } = {},
      invalidFields: string[] = null,
    ) => {
      const overlay = overlayRef.current;
      const area = areaRef.current;
      const { value } = area;

      const oldMatch = current.match;

      const currentIndex = scope.clearSelectionOnBlur ? undefined : current.match?.i;
      current.result = formatAll(value, currentIndex, invalidFields);
      current.matches = current.result.matches;
      current.complex = resultToComplex(current.result);
      overlay.innerHTML = current.result.html;

      if (scope.touched) {
        updateSelection({ triggerChanges, triggerSelection });
      } else {
        updateSelection();
      }

      const macro = api.getCurrentMacro();
      const complex = current.complex;
      complex.current = macro?.segment || current.text || '';

      if (triggerChanges) {
        const currentMatch = current.match;
        const hasMatches = Boolean(oldMatch && currentMatch);
        if (hasMatches && oldMatch.segment !== currentMatch.segment) {
          scope.onMacroChange?.(matchToMacro(current.match));
        }

        scope.onChange?.(complex);
      }
    };

    return {
      clearSelection,
      updateSelection,
      render,
    };
  });

  const api = useConst<MacroInputApi>(() => {
    const setValue = (value = '', triggerEvents = true) => {
      const area = areaRef.current;
      if (!area) return;

      if (area.value.trim() !== value.trim()) {
        area.value = value;
        actions.render(
          {
            triggerChanges: triggerEvents,
            triggerSelection: triggerEvents,
          },
          invalidFields,
        );
      }
    };
    const setSegments = (segments: (string | Macro | MacroParts)[], triggerEvents?: boolean) => {
      const area = areaRef.current;
      if (!area) return;

      const value = segmentsToString(segments);
      setValue(value, triggerEvents);
    };

    const getValue = () => areaRef.current?.value || '';
    const getMacros = () => current.complex.macros;
    const getTexts = () => current.complex.texts;
    const getSegments = () => current.complex.segments;
    const getCurrentComplex = () => structuredClone(current.complex);
    const getCurrentMacro = () => current.match && matchToMacro(current.match);
    const getCurrentText = () => current.text;
    const getCurrentTextInfo = () => ({
      text: current.text,
      start: current.textStart,
      end: current.textEnd,
    });
    const getCurrentSegment = () => {
      const area = areaRef.current;

      return {
        element: area,
        selectionStart: area ? area.selectionStart : 0,
        selectionEnd: area ? area.selectionEnd : 0,
        segment: current.text || current.match?.segment || '',
      };
    };
    const updateCurrentMacro = (segment: string) => {
      const currentMatch = current.match;
      const area = areaRef.current;

      if (!currentMatch || !area) return;

      segment = segment.trim();

      const value = area.value;
      const head = value.slice(0, currentMatch.start);
      const tail = value.slice(currentMatch.end);
      const space = tail.startsWith(' ') ? '' : ' ';

      const selectionTarget = currentMatch.start + segment.length;

      area.value = `${head}${segment}${space}${tail}`;
      area.selectionStart = selectionTarget;
      area.selectionEnd = selectionTarget;

      setTouched(true);
      scope.touched = true;
      actions.render({ triggerSelection: true, triggerChanges: true }, invalidFields);
    };
    const replaceCurrentText = (newSegment: string) => {
      const currentText = current.text;
      const area = areaRef.current;

      if (!currentText || !area) return;

      const currentValue = area.value;
      const newValue = [
        currentValue.slice(0, current.textStart),
        newSegment,
        currentValue.slice(current.textEnd) || (newSegment.endsWith(':') ? '' : ' '),
      ];

      const selectionTarget = (current.textStart || 0) + newSegment.length + (current.textEnd || 0);

      area.value = newValue.join('');
      area.selectionStart = selectionTarget;
      area.selectionEnd = selectionTarget;

      setTouched(true);
      scope.touched = true;
      actions.render({ triggerSelection: true, triggerChanges: true }, invalidFields);
    };
    const appendValue = (segment: string) => {
      const area = areaRef.current;
      const { value } = area;
      const { length } = value;

      if (!segment) return;

      if (length) {
        const space = value[length - 1] === ' ' ? '' : ' ';
        area.value = `${value}${space}${segment}`;
      } else {
        area.value = segment;
      }

      setTouched(true);
      scope.touched = true;
      actions.render({ triggerSelection: true, triggerChanges: true }, invalidFields);
    };
    const triggerChangeWithFakeValue = (
      value: string,
      onChange?: (e: RChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const area = areaRef.current as unknown as HTMLInputElement;
      const e = new Event('change', { bubbles: true });
      const rEvent = e as unknown as RChangeEvent<HTMLInputElement>;
      const fixedEvent = {
        ...rEvent,
        nativeEvent: e,
        currentTarget: area,
        target: area,
      };

      const { selectionStart, selectionEnd } = area;

      const oldValue = api.getValue();
      area.value = value;
      area?.dispatchEvent(e);

      onChange?.(fixedEvent);

      area.value = oldValue;
      area.selectionStart = selectionStart;
      area.selectionEnd = selectionEnd;
    };
    const triggerChangeWithCurrentSegment = (
      onChange?: (e: RChangeEvent<HTMLInputElement>) => void,
    ): void => {
      const { segment } = api.getCurrentSegment();
      triggerChangeWithFakeValue(segment, onChange);
    };
    const focus = () => areaRef.current?.focus();
    const blur = () => areaRef.current?.blur();
    const contains = (element: HTMLElement) => rootRef.current?.contains(element);

    return {
      setValue,
      setSegments,
      getValue,
      getMacros,
      getTexts,
      getSegments,
      getCurrentComplex,
      getCurrentMacro,
      getCurrentText,
      getCurrentTextInfo,
      getCurrentSegment,
      updateCurrentMacro,
      replaceCurrentText,
      appendValue,
      triggerChangeWithFakeValue,
      triggerChangeWithCurrentSegment,
      focus,
      blur,
      contains,
    };
  });

  useImperativeHandle(forwardedRef, () => api);

  useOnComponentMount(() => {
    const rootClasses = rootRef.current.classList;
    const area = areaRef.current;
    let inputChanged = false;

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.preventDefault();
      }

      const rEvent = e as unknown as RKeyboardEvent<HTMLInputElement>;
      scope.onKeyDown?.({
        ...rEvent,
        nativeEvent: e,
        currentTarget: area as unknown as HTMLInputElement,
        target: area as unknown as HTMLInputElement,
      });
    };

    const onKeyUp = (e: KeyboardEvent) => {
      $event: {
        if (inputChanged) {
          inputChanged = false;
          break $event;
        }

        if (!scope.touched) {
          break $event;
        }

        const { key } = e;
        switch (true) {
          case key === 'Alt':
          case key === 'Shift':
          case key === 'Meta':
          case key === 'Control':
          case key.startsWith('Arrow'):
            actions.updateSelection({
              triggerSelection: true,
              triggerChanges: true,
            });
        }
      }

      const rEvent = e as unknown as RKeyboardEvent<HTMLInputElement>;
      scope.onKeyUp?.({
        ...rEvent,
        nativeEvent: e,
        currentTarget: area as unknown as HTMLInputElement,
        target: area as unknown as HTMLInputElement,
      });
    };

    const onInput = () => {
      inputChanged = true;
      actions.render(
        {
          triggerSelection: true,
          triggerChanges: true,
        },
        invalidFields,
      );
    };

    const onClick = () => {
      if (!scope.touched) return;

      actions.updateSelection({
        triggerSelection: true,
        triggerChanges: true,
      });
    };

    const onFocus = (e: FocusEvent) => {
      rootClasses.add(focusedClassName);
      setTouched((scope.touched = true));

      const rEvent = e as unknown as RFocusEvent<HTMLInputElement>;
      rEvent.nativeEvent = e;
      scope.onFocus?.(rEvent);
    };

    const onBlur = (e: FocusEvent) => {
      rootClasses.remove(focusedClassName);

      if (scope.clearSelectionOnBlur) {
        current.text = '';
        actions.clearSelection({ triggerSelection: true });
      }

      const rEvent = e as unknown as RFocusEvent<HTMLInputElement>;
      rEvent.nativeEvent = e;
      scope.onBlur?.(rEvent);
    };

    area.addEventListener('focus', onFocus);
    area.addEventListener('blur', onBlur);
    area.addEventListener('click', onClick);
    area.addEventListener('keydown', onKeyDown);
    area.addEventListener('input', onInput);
    area.addEventListener('keyup', onKeyUp);

    return () => {
      area.removeEventListener('focus', onFocus);
      area.removeEventListener('blur', onBlur);
      area.removeEventListener('click', onClick);
      area.removeEventListener('keydown', onKeyDown);
      area.removeEventListener('input', onInput);
      area.removeEventListener('keyup', onKeyUp);
    };
  });

  useEffect(() => {
    const area = areaRef.current;
    const overlayRef = areaRef.current;
    if (!area || !overlayRef) return;

    setTouched(false);

    const valueMatches = area.value === value;
    if (!valueMatches || (valueMatches && !overlayRef.hasChildNodes())) {
      area.value = value;
      actions.render({}, invalidFields);
    }
  }, [value]);

  useDidUpdateEffect(() => {
    actions.render({}, invalidFields);
  }, [invalidFields]);

  useEffect(() => {
    if (clearSelectionOnBlur) {
      actions.clearSelection({ triggerSelection: true });
    }
  }, [clearSelectionOnBlur]);

  return (
    <CSSBox
      className={`macro-input ${multiLine ? multiClassName : ''}`}
      ref={rootRef}
      cssText={style}
    >
      <div className="macro-input-wrap">
        <div className="macro-input-overlay" ref={overlayRef} />
        <textarea
          {...nativeProps}
          className={`macro-input-area ${nativeProps.className || ''}`}
          spellCheck={false}
          ref={areaRef}
          rows={1}
        />
      </div>
      {before || <div />}
      {after || <div />}
    </CSSBox>
  );
};

export const MacroInput = forwardRef(MacroInputFn) as (
  props: MacroInputProps & { ref?: ForwardedRef<MacroInputApi> },
) => ReturnType<typeof MacroInputFn>;
